import React from "react";
import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { Trans } from "react-i18next";
import { Step } from "../types";
import { Steps } from "./Steps";
import ListLoading from "../../../util/Loading";
import PaymentStatus from "../Ui/PaymentStatus";
import { usePaymentState } from "../../../../hooks/usePaymentState";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    payment: {
      '& .venue': {
        padding: '1rem',

        '& .title': {
          margin: '0',
          marginBottom: '0.5rem',
          fontSize: '1.4rem',
          color: '#212121',
        },

        '& .description': {
          margin: '0',
          fontSize: '1rem',
          color: '#8D98B7',
          fontWeight: 'normal',
        },
      },

      '& .status': {
        padding: '1rem',
      },

      '& .wizard': {
        width: '100%',
        padding: '0.5rem',
        margin: '1rem 0',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        '& .wizardHeader':{
          padding:"1rem 3rem",
          display:"flex",
          alignItems:"center",
          justifyContent:"space-between",
          '& >div:first-child':{
            '& >p:first-child':{
              color:"#212121",
              fontSize:"1rem",
              fontWeight:"600"
            },
            '& >p':{
              color:"#333333",
              fontSize:"1rem",
              fontWeight:"normal"

            }
          }
        },
        '& .steps': {
          display: 'flex',
          flexDirection: 'column',
          borderBottom: '1px solid #f3f4f9',
          padding:"0 1rem",
          '& .step': {
            flexGrow: '10',
            marginBottom: '1rem',

            '& .icon': {
              width: '1.6rem',
              height: '1.6rem',
              borderRadius: '50%',
              margin: '0.5rem auto',
              backgroundPosition: 'center',
              backgroundSize: '0.5rem',
              backgroundRepeat: 'no-repeat',
              position: 'relative',

              '&::before': {
                content: "''",
                position: 'absolute',
                display: 'block',
                width: '160%',
                height: '160%',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: '#ffdd02',
                opacity: '0.35',
                borderRadius: '50%',
              },
            },

            '&.finished': {
              '& .icon': {
                backgroundColor: '#ffdd02',
                backgroundImage: 'url(/assets/images/icon-check.svg) !important',

                '&::before': {
                  display: 'none',
                },
              },

              '& .caption': {
                color: ' #aab6d3',

                '& a': {
                  textDecoration: 'none',
                  color: '#aab6d3',
                },
              },
            },

            '&.current': {
              '& .icon': {
                backgroundColor: '#ffdd02',
              },
            },

            '&.unfinished': {
              opacity: '0.6',

              '& .icon': {
                backgroundColor: '#E0E0E0',

                '&::before': {
                  display: 'none',
                },
              },
            },

            '&.current ,&.unfinished': {
              '&:nth-of-type(1)': {
                '& .icon': {
                  backgroundImage: 'url(/assets/images/icon-one.svg)',
                },
              },

              '&:nth-of-type(2)': {
                '& .icon': {
                  backgroundImage: 'url(/assets/images/icon-two.svg)',
                },
              },

              '&:nth-of-type(3)': {
                '& .icon': {
                  backgroundImage: 'url(/assets/images/icon-three.svg)',
                },
              },
            },

            '& .title': {
              textAlign: 'center',
              fontWeight: '600',
            },

            '& .caption': {
              color: '#616161',
              fontSize: '1rem',
              textAlign: 'left',
              padding: '1rem 2rem',

              '& a': {
                textDecoration: 'none',
                color: '#4F3CC9',
              },
            },
          },

          '@media only screen and (min-width: 960px)': {
            flexDirection: 'row',

            '& .step': {
              width: '100%',
            },
          },
        },

        '& .content': {
          fontSize: '1rem',
          padding: '2rem 3rem',
          "& .ContentRow": {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between"
          },
          "& .link": {
            '&:hover': {
              textDecoration: 'none',
            },
          },
        },
      },
    },

    ".indicator": {
      display: 'inline-flex',
      borderRadius: '8px',
      padding: '0.5rem 1rem',
      width: 'auto',

      '& span': {
        display: 'inline-block',
        lineHeight: '1.6rem',
        fontSize: '1rem',
        color: '#212121',
      },

      '& em': {
        display: 'inline-block',
        width: '1.6rem',
        height: '1.6rem',
        marginLeft: '1rem',
        borderRadius: '8px',
        backgroundPosition: 'center',
        backgroundSize: '100%',
        backgroundRepeat: 'no-repeat',
      },

      '&.warning': {
        backgroundColor: '#fff4e5',

        '& em': {
          backgroundImage: 'url("/assets/images/icon-square-warning.svg")',
        },
      },

      '&.info': {
        backgroundColor: '#e8f4fd',

        '& em': {
          backgroundImage: 'url("/assets/images/icon-square-info.svg")',
        },
      },

      '&.success': {
        backgroundColor: '#ffffff',

        '& em': {
          backgroundImage: 'url("/assets/images/icon-square-success.svg")',
        },
      },
    },
  })
);

const Wizard = () => {
  const {
    isError,
    paymentStage,
    paymentURL,
    paymentDetails,
  } = usePaymentState();
  const { t } = useTranslation();
  const steps = Steps();
  const step: Step = steps[paymentStage];
  const classes = useStyles();

  const renderSteps = () => {
    const clone = [...steps];

    if (!step.showSteps) {
      return null;
    }

    clone.pop();
    clone.pop();

    return (
      <div className="steps">
        {clone.map((item, index) => (
          <div
            className={`step ${index < paymentStage
              ? "finished"
              : index === paymentStage
                ? "current"
                : "unfinished"
              }`}
          >
            <div className="icon"></div>

            <div className="title">{item.title}</div>

            <div className="caption">{item.caption}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderButton = () => {
    return step.button.disabled ? null : (
      <a
        href={step.button.url}
        target={step.button.target}
        style={{ textDecoration: "none" }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={step.button.disabled}
        >
          {step.button.text}
          {step.button.icon}
        </Button>
      </a>
    );
  };

  const renderWizard = () => {
    return (
      <>
        <div className="wizard">
          <div className="wizardHeader">
          <div>
            <p>{t("payments.wizard.header.Payments")}</p>
            {
              paymentStage === 4 ? <p>
              <Trans i18nKey="payments.wizard.step5.content">
                0 <b>1</b> 2
              </Trans>
            </p> : paymentStage === 0 ? <p>{t("payments.wizard.header.setupYourAccount")}</p> : null
            }
          </div>
          <div className="ContentRow">
              <PaymentStatus
                message={step.indicator.text}
                status={step.indicator.icon}
              />
            </div>
          </div>
          {renderSteps()}
          <div className="content">

          {step.content}

            {renderButton()}
          </div>
        </div>
      </>
    );
  };

  if (isError) {
    return (
      <div>
        {t("payments.texts.error", {
          paymentProcessor: paymentDetails.paymentProcessor,
        })}
      </div>
    );
  }
  if (paymentStage < 0) {
    return <ListLoading />;
  }
  if (paymentURL === undefined) {
    return null;
  }
  return (
    <div className={classes.payment}>

      {renderWizard()}
    </div>
  );
};

export default Wizard;
